import { type FC, useState } from 'react';
import cx from 'classnames';

import { IconArrowLeft_24 } from 'src/assets/icons/24x24/IconArrowLeft_24';
import { IconArrowRight_24 } from 'src/assets/icons/24x24/IconArrowRight_24';
import { CUSTOM_ICON_BUTTON_SIZE, CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import {
  PROJECT_TEMPLATE_CARD_TYPE,
  ProjectTemplateCard,
  // @ts-expect-error - ProjectTemplateCard is not typed yet
} from 'src/components/ProjectTemplateCard';
import {
  type RecentlyUsedTemplateCardData,
  useRecentlyUsedTemplatesCards,
} from 'src/services/templates/useRecentlyUsedTemplatesCards';

import { WIDGET_ACTION, type WidgetAction, type WidgetActionData } from '../../lib';

import styles from './styles.module.scss';
import { useSupportScrolling } from './useSupportScrolling';

interface RecentlyUsedTemplatesProps {
  className?: string;
  onWidgetAction: (action: WidgetAction, data?: WidgetActionData) => void;
}

export const RecentlyUsedTemplates: FC<RecentlyUsedTemplatesProps> = ({
  className,
  onWidgetAction,
}) => {
  const [cardsElement, setCardsElement] = useState<HTMLElement | null>(null);

  const cards = useRecentlyUsedTemplatesCards();

  const { scrollState, handleScrollLeft, handleScrollRight } = useSupportScrolling(
    cardsElement,
    cards.length
  );

  const handleCardSelect = (card: RecentlyUsedTemplateCardData) => {
    onWidgetAction(WIDGET_ACTION.createRecentProject, { recentlyUsedTemplateCardData: card });
  };

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className={cx(styles.root, className)}>
      <header className={styles.header}>
        <CustomTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          Recently used
        </CustomTypography>

        <CustomIconButton
          className={styles.scrollButton}
          onClick={handleScrollLeft}
          disabled={!scrollState.canScrollLeft}
          size={CUSTOM_ICON_BUTTON_SIZE.small}
        >
          <IconArrowLeft_24 />
        </CustomIconButton>

        <CustomIconButton
          className={styles.scrollButton}
          onClick={handleScrollRight}
          disabled={!scrollState.canScrollRight}
          size={CUSTOM_ICON_BUTTON_SIZE.small}
        >
          <IconArrowRight_24 />
        </CustomIconButton>
      </header>

      <main className={styles.cardsWrapper}>
        <div
          className={styles.cards}
          ref={setCardsElement}
        >
          {cards.map((card) => (
            <ProjectTemplateCard
              className={styles.card}
              key={card.id}
              cardType={PROJECT_TEMPLATE_CARD_TYPE.medium}
              projectType={card.projectType}
              generationTool={card.generationTool}
              customToolData={card.customToolData}
              subTemplateData={card.subTemplateData}
              onSelect={() => handleCardSelect(card)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};
